import { img } from "@/assets/images";

export interface ServiceData {
  slug: string;
  title: string;
  subtitle: string;
  price: string;
  image: string;
  description: string;
  features: string[];
  process: { step: string; desc: string }[];
}

const cleaningProcess = (label: string): { step: string; desc: string }[] => [
  { step: "Free Estimate", desc: `Tell us about your space and we'll send a clear, no-pressure quote for your ${label}.` },
  { step: "Custom Plan", desc: "We build a checklist around your priorities, schedule, and the areas that matter most to you." },
  { step: "Professional Clean", desc: "Our trained, vetted crew arrives on time with their own supplies and gets to work with care." },
  { step: "Final Walkthrough", desc: "We review every room together so the result meets your standard before we consider the job done." },
];

export const services: ServiceData[] = [
  {
    slug: "residential-cleaning",
    title: "Residential Cleaning",
    subtitle: "Spotless homes, handled with care",
    price: "Free Quote",
    image: img.residentialCleaning,
    description:
      "Good Choice Cleaning brings dependable, detail-focused residential cleaning to homes across Harrisonburg and the surrounding Shenandoah Valley. Whether you need a one-time refresh or ongoing help, our trained team treats your home like their own — leaving every room fresh, sanitized, and genuinely clean.",
    features: [
      "Kitchens, bathrooms, bedrooms, and living areas",
      "Dusting, vacuuming, mopping, and surface sanitizing",
      "Eco-friendly products available on request",
      "Flexible weekly, biweekly, or monthly schedules",
      "Trained, background-checked cleaning professionals",
      "Satisfaction-focused final walkthrough",
    ],
    process: cleaningProcess("home"),
  },
  {
    slug: "commercial-cleaning",
    title: "Commercial Cleaning",
    subtitle: "Clean spaces that impress your clients",
    price: "Free Quote",
    image: img.commercialCleaning,
    description:
      "A clean business builds trust. Good Choice Cleaning provides reliable commercial cleaning for offices, retail spaces, clinics, and facilities throughout the Harrisonburg area. We work around your hours to keep your space presentable, hygienic, and ready for customers and staff every single day.",
    features: [
      "Offices, retail, clinics, and commercial facilities",
      "After-hours and flexible scheduling available",
      "Restroom sanitizing and restocking",
      "High-touch surface disinfection",
      "Trash removal and recycling handling",
      "Consistent, dependable recurring service",
    ],
    process: cleaningProcess("commercial space"),
  },
  {
    slug: "house-cleaning",
    title: "House Cleaning",
    subtitle: "A fresh, comfortable home every time",
    price: "Free Quote",
    image: img.houseCleaning,
    description:
      "Reclaim your weekends with professional house cleaning from Good Choice Cleaning. From top to bottom, we handle the dusting, scrubbing, and tidying so you can come home to a space that feels calm, fresh, and truly clean — without lifting a finger.",
    features: [
      "Whole-home cleaning, room by room",
      "Kitchen and bathroom deep-sanitizing",
      "Floors vacuumed, swept, and mopped",
      "Dusting of surfaces, fixtures, and baseboards",
      "Customizable checklist to fit your home",
      "Reliable crews you can trust in your space",
    ],
    process: cleaningProcess("house cleaning"),
  },
  {
    slug: "office-cleaning",
    title: "Office Cleaning",
    subtitle: "A workspace your team will love",
    price: "Free Quote",
    image: img.officeCleaning,
    description:
      "A tidy office keeps your team productive and your clients confident. Good Choice Cleaning delivers thorough, discreet office cleaning tailored to your workflow — desks, common areas, break rooms, and restrooms kept spotless so you can focus on running your business.",
    features: [
      "Workstations, common areas, and break rooms",
      "Restroom cleaning and restocking",
      "Floor care for carpet and hard surfaces",
      "Kitchen and pantry sanitizing",
      "Daily, weekly, or custom schedules",
      "Discreet, professional on-site conduct",
    ],
    process: cleaningProcess("office cleaning"),
  },
  {
    slug: "deep-cleaning",
    title: "Deep Cleaning",
    subtitle: "A top-to-bottom reset for your space",
    price: "Free Quote",
    image: img.deepCleaning,
    description:
      "When a standard clean isn't enough, our deep cleaning service gets into the details others miss. Good Choice Cleaning tackles built-up grime, hidden dust, and neglected corners — baseboards, appliances, grout, and behind-the-scenes areas — for a complete, refreshing reset of your home or business.",
    features: [
      "Detailed scrubbing of kitchens and bathrooms",
      "Baseboards, door frames, and vents",
      "Inside appliances on request",
      "Grout, tile, and hard-to-reach areas",
      "Cabinet fronts and light fixtures",
      "Ideal before events or seasonal refreshes",
    ],
    process: cleaningProcess("deep cleaning"),
  },
  {
    slug: "regular-cleaning",
    title: "Regular Cleaning",
    subtitle: "Consistent upkeep, zero hassle",
    price: "Free Quote",
    image: img.regularCleaning,
    description:
      "Keep your home effortlessly clean with recurring service from Good Choice Cleaning. Our regular cleaning keeps dust, clutter, and grime from ever building up — with the same trusted crew, a consistent checklist, and scheduling that fits your routine.",
    features: [
      "Weekly, biweekly, or monthly visits",
      "Consistent checklist every visit",
      "Same trusted crew whenever possible",
      "Kitchens, baths, floors, and living areas",
      "Returning-customer friendly scheduling",
      "Easy to adjust as your needs change",
    ],
    process: cleaningProcess("regular cleaning"),
  },
  {
    slug: "move-out-cleaning",
    title: "Move-Out Cleaning",
    subtitle: "Leave it spotless, get your deposit back",
    price: "Free Quote",
    image: img.moveOutCleaning,
    description:
      "Moving is stressful enough. Good Choice Cleaning handles the move-out clean so you can hand over the keys with confidence. We clean every room to a landlord- and buyer-ready standard, helping you protect your deposit and leave the property looking its very best.",
    features: [
      "Full property clean, empty or furnished",
      "Inside cabinets, drawers, and closets",
      "Appliance interiors and exteriors",
      "Bathrooms and kitchens fully sanitized",
      "Baseboards, floors, and fixtures detailed",
      "Landlord and realtor-ready results",
    ],
    process: cleaningProcess("move-out cleaning"),
  },
  {
    slug: "move-in-cleaning",
    title: "Move-In Cleaning",
    subtitle: "Start fresh in a truly clean home",
    price: "Free Quote",
    image: img.moveInCleaning,
    description:
      "Before you unpack a single box, let Good Choice Cleaning make your new place shine. Our move-in cleaning sanitizes every surface, cabinet, and floor so your new home feels fresh, healthy, and ready for you from day one.",
    features: [
      "Deep clean before you move in",
      "Cabinets and drawers wiped inside and out",
      "Kitchens and bathrooms fully sanitized",
      "Floors vacuumed, swept, and mopped",
      "Dust and debris removed throughout",
      "A healthy, fresh start in your new space",
    ],
    process: cleaningProcess("move-in cleaning"),
  },
  {
    slug: "apartment-cleaning",
    title: "Apartment Cleaning",
    subtitle: "Compact spaces, spotless results",
    price: "Free Quote",
    image: img.apartmentCleaning,
    description:
      "Apartments deserve the same attention as any home. Good Choice Cleaning offers efficient, thorough apartment cleaning for renters and owners across the Harrisonburg area — making the most of every square foot so your space feels open, fresh, and comfortable.",
    features: [
      "Studio to multi-bedroom apartments",
      "Kitchen and bathroom sanitizing",
      "Floors, surfaces, and fixtures cleaned",
      "Great for lease renewals and turnovers",
      "One-time or recurring service",
      "Efficient, respectful, on-time crews",
    ],
    process: cleaningProcess("apartment cleaning"),
  },
  {
    slug: "windows-cleaning",
    title: "Window Cleaning",
    subtitle: "Streak-free views, inside and out",
    price: "Free Quote",
    image: img.windowsCleaning,
    description:
      "Let the light back in. Good Choice Cleaning delivers streak-free window cleaning for homes and businesses, brightening every room and boosting your curb appeal. We handle glass, sills, and frames for a crystal-clear finish you'll notice right away.",
    features: [
      "Interior and exterior window glass",
      "Sills, tracks, and frames wiped down",
      "Streak-free, spot-free finish",
      "Residential and commercial windows",
      "Screens cleaned on request",
      "Brighter rooms and better curb appeal",
    ],
    process: cleaningProcess("window cleaning"),
  },
  {
    slug: "carpet-cleaning",
    title: "Carpet Cleaning",
    subtitle: "Fresh, revived carpets and rugs",
    price: "Free Quote",
    image: img.carpetCleaning,
    description:
      "Carpets trap dust, allergens, and stains over time. Good Choice Cleaning refreshes your carpets and rugs, lifting embedded dirt and reviving color and texture so your floors feel soft, clean, and healthy underfoot again.",
    features: [
      "Deep extraction of dirt and allergens",
      "Spot and stain treatment",
      "Odor-neutralizing finish",
      "Safe for homes with kids and pets",
      "Residential and commercial carpets",
      "Rugs and high-traffic areas revived",
    ],
    process: cleaningProcess("carpet cleaning"),
  },
  {
    slug: "construction-cleaning",
    title: "Construction Clean up",
    subtitle: "From job site to move-in ready",
    price: "Free Quote",
    image: img.constructionCleaning,
    description:
      "After the build comes the dust. Good Choice Cleaning specializes in post-construction cleanup, clearing debris, fine dust, and residue so your newly built or renovated space is safe, polished, and ready to occupy. We coordinate with contractors and homeowners alike.",
    features: [
      "Post-construction dust and debris removal",
      "Fine dust wiped from every surface",
      "Windows, fixtures, and floors detailed",
      "Paint, adhesive, and residue cleanup",
      "Coordinated with your build timeline",
      "Move-in and inspection-ready results",
    ],
    process: cleaningProcess("post-construction cleaning"),
  },
  {
    slug: "airbnb-cleaning",
    title: "Airbnb Cleaning",
    subtitle: "Five-star ready between every guest",
    price: "Free Quote",
    image: img.airbnbCleaning,
    description:
      "Great reviews start with a spotless space. Good Choice Cleaning provides fast, reliable Airbnb and short-term rental cleaning with hotel-level attention to detail — fresh linens, sanitized surfaces, and a guest-ready finish that keeps your ratings high and your calendar full.",
    features: [
      "Turnover cleaning between guests",
      "Fresh linens and made beds",
      "Bathrooms and kitchens sanitized",
      "Restocking of essentials on request",
      "Flexible, same-day scheduling",
      "Hotel-standard, five-star finish",
    ],
    process: cleaningProcess("Airbnb cleaning"),
  },
  {
    slug: "turns-cleaning",
    title: "Turnover Cleaning",
    subtitle: "Fast, reliable unit turnarounds",
    price: "Free Quote",
    image: img.turnsCleaning,
    description:
      "Property managers and landlords trust Good Choice Cleaning for quick, thorough turnover cleaning between tenants. We reset each unit to a clean, rentable standard on your timeline — helping you reduce vacancy and welcome new tenants without delay.",
    features: [
      "Complete unit reset between tenants",
      "Kitchens, baths, and floors detailed",
      "Cabinets and appliances cleaned",
      "Fast turnaround to reduce vacancy",
      "Volume scheduling for multiple units",
      "Consistent, rentable-ready standard",
    ],
    process: cleaningProcess("turnover cleaning"),
  },
  {
    slug: "janitorial-cleaning",
    title: "Janitorial Cleaning",
    subtitle: "Ongoing care for busy facilities",
    price: "Free Quote",
    image: img.janitorialCleaning,
    description:
      "Keep your facility running clean with dependable janitorial service from Good Choice Cleaning. From schools and clinics to offices and warehouses, we provide consistent, scheduled maintenance that keeps your environment healthy, professional, and always presentable.",
    features: [
      "Scheduled recurring janitorial service",
      "Restroom sanitizing and restocking",
      "Floor care, trash removal, and dusting",
      "High-touch surface disinfection",
      "Schools, clinics, offices, and warehouses",
      "Reliable crews and consistent quality",
    ],
    process: cleaningProcess("janitorial service"),
  },
];
